// src/game/gameAPI.js
// ============================================
// ارتباط با سرور Colyseus (نسخه نهایی)
// ============================================

import { Client } from 'colyseus.js';

// ============================================
// ۱. تنظیمات
// ============================================

const SERVER_URL = import.meta.env.VITE_GAME_SERVER_URL || 'ws://localhost:2567';

let client = null;
let room = null;
let eventHandlers = {};
let isConnected = false;

// ============================================
// ۲. اتصال به سرور
// ============================================

export const connectToServer = async (username, mode = 'random') => {
  try {
    // ایجاد کلاینت
    if (!client) {
      client = new Client(SERVER_URL);
    }

    // ورود به اتاق بازی
    room = await client.joinOrCreate('game_room', {
      username: username || 'کاربر',
      mode: mode
    });

    isConnected = true;
    console.log(`✅ متصل به اتاق ${room.id}`);

    // تنظیم شنونده‌های رویداد
    setupEventListeners();

    return { success: true, roomId: room.id };
  } catch (error) {
    console.error('❌ خطا در اتصال به سرور:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// ۳. تنظیم شنونده‌های رویداد
// ============================================

const setupEventListeners = () => {
  if (!room) return;

  // رویداد: شروع بازی
  room.onMessage('game_started', (data) => {
    if (eventHandlers.game_started) eventHandlers.game_started(data);
  });

  // رویداد: حریف پیدا شد
  room.onMessage('opponent_found', (data) => {
    if (eventHandlers.opponent_found) eventHandlers.opponent_found(data);
  });

  // رویداد: سوال جدید
  room.onMessage('new_question', (data) => {
    if (eventHandlers.new_question) eventHandlers.new_question(data);
  });

  // رویداد: بروزرسانی تایمر
  room.onMessage('timer_update', (data) => {
    if (eventHandlers.timer_update) eventHandlers.timer_update(data);
  });

  // رویداد: نتیجه پاسخ
  room.onMessage('answer_result', (data) => {
    if (eventHandlers.answer_result) eventHandlers.answer_result(data);
  });

  // رویداد: بازیکن پاسخ داد
  room.onMessage('player_answered', (data) => {
    if (eventHandlers.player_answered) eventHandlers.player_answered(data);
  });

  // رویداد: پایان بازی
  room.onMessage('game_finished', (data) => {
    if (eventHandlers.game_finished) eventHandlers.game_finished(data);
  });

  // رویداد: بازیکن تسلیم شد
  room.onMessage('player_surrendered', (data) => {
    if (eventHandlers.player_surrendered) eventHandlers.player_surrendered(data);
  });

  // رویداد: بازیکن خارج شد
  room.onMessage('player_left', (data) => {
    if (eventHandlers.player_left) eventHandlers.player_left(data);
  });

  // رویداد: اتمام مرحله
  room.onMessage('stage_completed', (data) => {
    if (eventHandlers.stage_completed) eventHandlers.stage_completed(data);
  });

  // رویداد: انتخاب موضوع
  room.onMessage('category_selected', (data) => {
    if (eventHandlers.category_selected) eventHandlers.category_selected(data);
  });

  // رویداد: استفاده از آیتم کمکی
  room.onMessage('help_used', (data) => {
    if (eventHandlers.help_used) eventHandlers.help_used(data);
  });

  // رویداد: اتمام زمان
  room.onMessage('timeout', (data) => {
    if (eventHandlers.timeout) eventHandlers.timeout(data);
  });

  // رویداد: خطا
  room.onMessage('error', (data) => {
    if (eventHandlers.error) eventHandlers.error(data);
  });
};

// ============================================
// ۴. ثبت و حذف شنونده‌ها
// ============================================

export const on = (event, callback) => {
  eventHandlers[event] = callback;
};

export const off = (event) => {
  delete eventHandlers[event];
};

// ============================================
// ۵. ارسال پیام به سرور
// ============================================

export const sendMessage = (type, data) => {
  if (!room) return;
  room.send(type, data);
};

// ============================================
// ۶. توابع بازی
// ============================================

export const sendAnswer = (answerIndex) => {
  sendMessage('answer', { answer: answerIndex });
};

export const sendSurrender = () => {
  sendMessage('surrender', {});
};

export const sendCategory = (category) => {
  sendMessage('select_category', { category });
};

export const sendHelp = (helpType) => {
  sendMessage('use_help', { helpType });
};

export const sendCategories = (categories) => {
  sendMessage('select_categories', { categories });
};

export const nextQuestion = () => {
  sendMessage('next_question', {});
};

// ============================================
// ۷. قطع اتصال
// ============================================

export const disconnect = () => {
  if (room) {
    room.leave();
    room = null;
  }
  client = null;
  isConnected = false;
  eventHandlers = {};
  console.log('🔌 اتصال قطع شد');
};

// ============================================
// ۸. وضعیت اتصال
// ============================================

export const getRoomId = () => room?.id || null;
export const isRoomConnected = () => isConnected && room !== null;

// ============================================
// ۹. APIهای REST (برای آینده)
// ============================================

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:2567';

export const restAPI = {
  getUser: async (userId) => {
    const response = await fetch(`${API_URL}/api/user/${userId}`);
    return response.json();
  },
  getQuestions: async (category, count = 18) => {
    const response = await fetch(`${API_URL}/api/questions/${category}?count=${count}`);
    return response.json();
  },
  getWeeklyLeague: async () => {
    const response = await fetch(`${API_URL}/api/league/weekly`);
    return response.json();
  }
};

// ============================================
// ۱۰. صادرات پیش‌فرض
// ============================================

export default {
  connectToServer,
  on,
  off,
  sendAnswer,
  sendSurrender,
  sendCategory,
  sendCategories,
  sendHelp,
  nextQuestion,
  disconnect,
  getRoomId,
  isRoomConnected,
  restAPI
};